import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import { customerService } from "@/lib/database";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/lib/supabase";
import { handleError } from "@/lib/errors";
import * as Sentry from '@sentry/browser'

export default function AddCustomer() {
	// New customer form state
	const [newCustomer, setNewCustomer] = useState({
		first_name: "",
		last_name: "",
		email: "",
		phone: "",
		us_address: "",
		haiti_address: "",
		preferred_language: "english" as "english" | "french" | "creole",
		notification_method: "email" as "email" | "sms" | "both",
		preferred_shipping_method: "air" as "air" | "sea",
		createAuth: true,
		notificationMethod: "email" as "email" | "sms" | "both",
	});

	const navigate = useNavigate();

	const handleCreateCustomer = async () => {
		// Start a span for the entire operation
		return await Sentry.startSpan({
			name: "Create Customer Workflow",
			op: "ui.workflow",
		}, async () => {
			if (
				!newCustomer.first_name ||
				!newCustomer.last_name ||
				!newCustomer.email
			) {
				alert("Please fill in all required fields");
				return;
			}

			const { createAuth, notificationMethod, ...customerData } = newCustomer;

			if (createAuth) {
				// TODO: Move this into the customer service
				const { error: invokeError, data, } = await supabase.functions.invoke("create-user", {
					method: "POST",
					headers: {
						// Manually inject the current trace context into headers
						...Sentry.getTraceData(),
					},
					body: JSON.stringify({
						user: customerData,
						notificationMethod,
						userType: "customer",
					}),
				});

				let errorPayload = null;

				if (invokeError) {
					// Scenario A: Function returned 4xx or 500
					// We extract the JSON body from the context
					errorPayload = await invokeError.context.json();
				} else if (data && !data.ok) {
					// Scenario B: Function returned 200 or 202
					// Supabase returns 'data' as an object
					errorPayload = data; // This handles the 202 "signupError" case
				}

				if (errorPayload) {
					// Now we have a unified error object regardless of the status code
					alert(await handleError(errorPayload));
					return;
				}
			} else {
				await customerService.create(customerData, false);
			}

			setNewCustomer({
				first_name: "",
				last_name: "",
				email: "",
				phone: "",
				us_address: "",
				haiti_address: "",
				preferred_language: "english",
				notification_method: "email",
				preferred_shipping_method: "air",
				createAuth: true,
				notificationMethod: "email",
			});

			if (createAuth) {
				alert(
					`Customer account created successfully! Login credentials have been sent via ${notificationMethod === "both"
						? "both email and sms"
						: notificationMethod
					}.`
				);
			} else {
				alert("Customer profile created successfully!");
			}
		})
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex space-x-4">
					<Button variant="ghost" onClick={() => navigate(-1)}>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Cancel
					</Button>
					<div>
						<h1 className="text-2xl md:text-3xl font-bold text-gray-900">
							Add New Customer
						</h1>
						{/* <p className="text-gray-600">Update customer information</p> */}
					</div>
				</div>

				{/* <Button onClick={handle} disabled={saving} className="bg-[#254164] hover:bg-[#1a2d47]">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button> */}
			</div>

			<div className="space-y-2">
				<Card>
					<CardHeader hidden>
						<CardDescription>
							Fill in the details below to create a new customer profile.
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-6">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label htmlFor="first_name">First Name *</Label>
								<Input
									id="first_name"
									value={newCustomer.first_name}
									onChange={(e) =>
										setNewCustomer((prev) => ({
											...prev,
											first_name: e.target.value,
										}))
									}
								/>
							</div>
							<div>
								<Label htmlFor="last_name">Last Name *</Label>
								<Input
									id="last_name"
									value={newCustomer.last_name}
									onChange={(e) =>
										setNewCustomer((prev) => ({
											...prev,
											last_name: e.target.value,
										}))
									}
								/>
							</div>
						</div>
						<div>
							<Label htmlFor="email">Email *</Label>
							<Input
								id="email"
								type="email"
								value={newCustomer.email}
								onChange={(e) =>
									setNewCustomer((prev) => ({ ...prev, email: e.target.value }))
								}
							/>
						</div>
						<div>
							<Label htmlFor="phone">Phone</Label>
							<Input
								id="phone"
								value={newCustomer.phone}
								onChange={(e) =>
									setNewCustomer((prev) => ({ ...prev, phone: e.target.value }))
								}
							/>
						</div>
						<div>
							<Label htmlFor="us_address">US Address</Label>
							<Input
								id="us_address"
								value={newCustomer.us_address}
								onChange={(e) =>
									setNewCustomer((prev) => ({
										...prev,
										us_address: e.target.value,
									}))
								}
							/>
						</div>
						<div>
							<Label htmlFor="haiti_address">Haiti Address</Label>
							<Input
								id="haiti_address"
								value={newCustomer.haiti_address}
								onChange={(e) =>
									setNewCustomer((prev) => ({
										...prev,
										haiti_address: e.target.value,
									}))
								}
							/>
						</div>
						<div className="flex items-center space-x-2">
							<Switch
								id="create-auth"
								checked={newCustomer.createAuth}
								onCheckedChange={(checked) =>
									setNewCustomer((prev) => ({ ...prev, createAuth: checked }))
								}
							/>
							<Label htmlFor="create-auth">
								Create login account and send credentials
							</Label>
						</div>
						{newCustomer.createAuth && (
							<div className="space-y-2">
								<Label>Send credentials via:</Label>
								<RadioGroup
									value={newCustomer.notificationMethod}
									onValueChange={(value: "email" | "sms") =>
										setNewCustomer((prev) => ({
											...prev,
											notificationMethod: value,
										}))
									}
								>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="email" id="email-notification" />
										<Label htmlFor="email-notification">Email</Label>
									</div>
									<div className="flex items-center space-x-2">
										<RadioGroupItem
											disabled
											value="sms"
											id="sms-notification"
										/>
										<Label htmlFor="sms-notification">Text Message</Label>
									</div>
								</RadioGroup>
							</div>
						)}
						<Button onClick={handleCreateCustomer} className="w-full">
							Create Customer
						</Button>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
